'use client';

import type { ListQueryCodeApiDto } from '__generated__/api/unauth/types.gen';
import { Divider } from '@common/components';
import { useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';

import type { FilterOption } from '../../constants';
import { FilterCheckboxList } from '../FilterCheckboxList';
import { FilterChipGroup } from '../FilterChipGroup';
import { FilterDrawerShell } from '../FilterDrawerShell';
import { FilterSection } from '../FilterSection';
import { FilterSidebarShell } from '../FilterSidebarShell';

interface PolicyFilterProps {
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
  onApplyDrawer: () => void;
  targetCodes: string[];
  provisionCodes: string[];
  jobCodes: string[];
  ageCodes: string[];
  onToggleTargetCode: (code: string) => void;
  onToggleProvisionCode: (code: string) => void;
  onToggleJobCode: (code: string) => void;
  onToggleAgeCode: (code: string) => void;
  onReset: () => void;
}

const toFilterOptions = (
  codes?: Array<ListQueryCodeApiDto>,
): FilterOption<string>[] =>
  (codes ?? [])
    .filter((item): item is Required<Pick<ListQueryCodeApiDto, 'code' | 'value'>> =>
      Boolean(item.code && item.value),
    )
    .map((item) => ({ value: item.code, label: item.value }));

export function PolicyFilter({
  isDrawerOpen,
  onCloseDrawer,
  onApplyDrawer,
  targetCodes,
  provisionCodes,
  jobCodes,
  ageCodes,
  onToggleTargetCode,
  onToggleProvisionCode,
  onToggleJobCode,
  onToggleAgeCode,
  onReset,
}: PolicyFilterProps) {
  const { data: targetOptions = [], isLoading: isTargetLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/policies/codes/target'](),
    select: (data) => toFilterOptions(data.context?.queryCodes),
  });

  const { data: provisionOptions = [], isLoading: isProvisionLoading } =
    useQuery({
      ...UnauthQueryOptions[
        'get:/wello/v2/allows/query/policies/codes/provision'
      ](),
      select: (data) => toFilterOptions(data.context?.queryCodes),
    });

  const { data: jobOptions = [], isLoading: isJobLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/policies/codes/job'](),
    select: (data) => toFilterOptions(data.context?.queryCodes),
  });

  const { data: ageOptions = [], isLoading: isAgeLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/policies/codes/age'](),
    select: (data) => toFilterOptions(data.context?.queryCodes),
  });

  return (
    <>
      <FilterSidebarShell onReset={onReset}>
        <FilterSection title="대상">
          <FilterCheckboxList
            options={targetOptions}
            selectedValues={targetCodes}
            onToggle={onToggleTargetCode}
            isLoading={isTargetLoading}
            skeletonCount={6}
          />
        </FilterSection>

        <div className="py-8">
          <Divider />
        </div>

        <FilterSection title="지원형태">
          <FilterCheckboxList
            options={provisionOptions}
            selectedValues={provisionCodes}
            onToggle={onToggleProvisionCode}
            isLoading={isProvisionLoading}
            skeletonCount={6}
          />
        </FilterSection>

        <div className="py-8">
          <Divider />
        </div>

        <FilterSection title="직업">
          <FilterCheckboxList
            options={jobOptions}
            selectedValues={jobCodes}
            onToggle={onToggleJobCode}
            isLoading={isJobLoading}
            skeletonCount={6}
          />
        </FilterSection>

        <div className="py-8">
          <Divider />
        </div>

        <FilterSection title="연령">
          <FilterCheckboxList
            options={ageOptions}
            selectedValues={ageCodes}
            onToggle={onToggleAgeCode}
            isLoading={isAgeLoading}
            skeletonCount={6}
          />
        </FilterSection>
      </FilterSidebarShell>

      <FilterDrawerShell
        isOpen={isDrawerOpen}
        onReset={onReset}
        onApply={onApplyDrawer}
        onClose={onCloseDrawer}
      >
        <div className="flex flex-col gap-12">
          <h3 className="font-700 text-Gray-900 typo-body4">대상</h3>
          <FilterChipGroup
            options={targetOptions}
            selectedValues={targetCodes}
            onToggle={onToggleTargetCode}
            isLoading={isTargetLoading}
            skeletonCount={6}
          />
        </div>
        <div className="flex flex-col gap-12">
          <h3 className="font-700 text-Gray-900 typo-body4">지원형태</h3>
          <FilterChipGroup
            options={provisionOptions}
            selectedValues={provisionCodes}
            onToggle={onToggleProvisionCode}
            isLoading={isProvisionLoading}
            skeletonCount={6}
          />
        </div>
        <div className="flex flex-col gap-12">
          <h3 className="font-700 text-Gray-900 typo-body4">직업</h3>
          <FilterChipGroup
            options={jobOptions}
            selectedValues={jobCodes}
            onToggle={onToggleJobCode}
            isLoading={isJobLoading}
            skeletonCount={6}
          />
        </div>
        <div className="flex flex-col gap-12">
          <h3 className="font-700 text-Gray-900 typo-body4">연령</h3>
          <FilterChipGroup
            options={ageOptions}
            selectedValues={ageCodes}
            onToggle={onToggleAgeCode}
            isLoading={isAgeLoading}
            skeletonCount={6}
          />
        </div>
      </FilterDrawerShell>
    </>
  );
}
